import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collectDueDailyNotifications, currentDailyNotificationCursor, type DailyNotificationEvent } from "../disciplio/daily-notification-utils";
import { getTodayKey } from "../disciplio/logic";
import { useDisciplio } from "../context/disciplio-context";
import { useI18n } from "../context/i18n-context";
import { getWebPushConfig } from "../api/push-notifications-api";
import {
  browserNotificationState,
  currentBrowserPushSubscription,
  isDisciplioPageFocused,
  registerNotificationServiceWorker,
  showBrowserNotification,
} from "./browser-notifications";
import { playReminderChime, primeReminderChime } from "./reminder-chime";
import { Presence, presenceStateProps, usePresenceState } from "./ui/presence";

type Notice = DailyNotificationEvent & { createdAt: number; closing?: boolean };

/** Keep in-app reminder cards readable longer than a glance. */
const NOTICE_VISIBLE_MS = 20_000;

function ReminderIcon({ kind }: { kind: DailyNotificationEvent["kind"] }) {
  return kind === "START" ? (
    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.25" /><path d="M12 7.8v4.7l3.1 1.9" /></svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.4 8.3a4.6 4.6 0 0 1 9.2 0c0 5 2 5.4 2 7.1H5.4c0-1.7 2-2.1 2-7.1Z" /><path d="M9.7 18.1h4.6" /></svg>
  );
}

function CloseIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>;
}

export function DailyGoalNotifications() {
  const { state, ensurePlanLoaded } = useDisciplio();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [notices, setNotices] = useState<Notice[]>([]);
  const cursorRef = useRef<ReturnType<typeof currentDailyNotificationCursor> | null>(null);
  const seenRef = useRef(new Set<string>());
  const settings = state.settings;
  const [webPushActive, setWebPushActive] = useState(false);
  const [notificationDateKey, setNotificationDateKey] = useState(() => getTodayKey(settings));
  const items = useMemo(() => state.dailyItemsByDate[notificationDateKey] ?? [], [notificationDateKey, state.dailyItemsByDate]);

  useEffect(() => {
    let active = true;
    void (async () => {
      await registerNotificationServiceWorker();
      try {
        const config = await getWebPushConfig();
        const subscription = config.available ? await currentBrowserPushSubscription() : null;
        if (active) setWebPushActive(Boolean(subscription));
      } catch {
        if (active) setWebPushActive(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const prime = () => { void primeReminderChime(); };
    window.addEventListener("pointerdown", prime, { once: true });
    window.addEventListener("keydown", prime, { once: true });
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
  }, []);

  useEffect(() => {
    if (!settings.dailyGoalNotificationsEnabled) return;
    void ensurePlanLoaded(notificationDateKey);
  }, [ensurePlanLoaded, notificationDateKey, settings.dailyGoalNotificationsEnabled]);

  useEffect(() => {
    setNotificationDateKey(getTodayKey(settings));
  }, [settings]);

  const deliver = useCallback((events: DailyNotificationEvent[]) => {
    const unseen = events.filter((event) => !seenRef.current.has(event.id));
    if (!unseen.length) return;
    unseen.forEach((event) => seenRef.current.add(event.id));
    setNotices((current) => [...current, ...unseen.map((event) => ({ ...event, createdAt: Date.now() }))]);

    const focused = isDisciplioPageFocused();
    if (settings.notificationSoundEnabled && (focused || !webPushActive)) {
      void playReminderChime();
    }
    // OS banners while a tab is open but not focused. Closed-tab delivery is Web Push only.
    if (!focused && browserNotificationState() === "granted") {
      unseen.forEach((event) => void showBrowserNotification(event, settings.dailyGoalEndReminderMinutes));
    }
  }, [settings.dailyGoalEndReminderMinutes, settings.notificationSoundEnabled, webPushActive]);

  const checkForReminders = useCallback((initial = false) => {
    if (!settings.dailyGoalNotificationsEnabled) return;
    const current = currentDailyNotificationCursor(settings);
    if (current.dateKey !== notificationDateKey) {
      setNotificationDateKey(current.dateKey);
      return;
    }
    const previous = cursorRef.current ?? {
      ...current,
      operationalMinute: initial ? current.operationalMinute - 1 : current.operationalMinute,
    };
    const next = collectDueDailyNotifications(items, settings, previous);
    cursorRef.current = next.cursor;
    deliver(next.events);
  }, [deliver, items, notificationDateKey, settings]);

  useEffect(() => {
    cursorRef.current = null;
    seenRef.current.clear();
  }, [notificationDateKey]);

  useEffect(() => {
    checkForReminders(true);
    const interval = window.setInterval(() => checkForReminders(), 15_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForReminders();
    };
    window.addEventListener("focus", onVisibilityChange);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onVisibilityChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [checkForReminders]);

  useEffect(() => {
    if (!notices.length) return;
    const timers = notices.filter((notice) => !notice.closing).map((notice) => window.setTimeout(() => {
      setNotices((current) => current.map((entry) => entry.id === notice.id ? { ...entry, closing: true } : entry));
    }, Math.max(0, notice.createdAt + NOTICE_VISIBLE_MS - Date.now())));
    return () => timers.forEach(window.clearTimeout);
  }, [notices]);

  const dismissNotice = useCallback((id: string) => {
    setNotices((current) => current.map((notice) => notice.id === id ? { ...notice, closing: true } : notice));
  }, []);

  const removeNotice = useCallback((id: string) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  if (!notices.length) return null;

  return (
    <section className="daily-notification-stack" aria-live="polite" aria-label={t("notifications.liveLabel")}>
      {notices.slice(0, 3).map((notice) => (
        <Presence key={notice.id} open={!notice.closing} exitDurationMs={220} onExited={() => removeNotice(notice.id)}>
          <DailyNotificationCard
            notice={notice}
            reminderMinutes={settings.dailyGoalEndReminderMinutes}
            onOpen={() => navigate("/")}
            onClose={() => dismissNotice(notice.id)}
          />
        </Presence>
      ))}
    </section>
  );
}

function DailyNotificationCard({
  notice,
  reminderMinutes,
  onOpen,
  onClose,
}: {
  notice: Notice;
  reminderMinutes: number;
  onOpen: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const presenceState = usePresenceState();
  return (
    <article className="daily-notification" {...presenceStateProps(presenceState)}>
      <span className={`daily-notification__signal daily-notification__signal--${notice.kind.toLowerCase()}`} aria-hidden="true">
        <ReminderIcon kind={notice.kind} />
      </span>
      <div className="daily-notification__copy">
        <p className="daily-notification__eyebrow">
          {notice.kind === "START"
            ? t("notifications.startNow", { time: notice.scheduledTime })
            : t("notifications.endsSoon", { minutes: reminderMinutes, time: notice.scheduledTime })}
        </p>
        <strong>{notice.itemTitle}</strong>
      </div>
      <div className="daily-notification__actions">
        <button type="button" className="daily-notification__open" onClick={onOpen}>
          {t("notifications.openToday")}
        </button>
        <button type="button" className="daily-notification__close" onClick={onClose} aria-label={t("notifications.dismiss")} title={t("notifications.dismiss")}>
          <CloseIcon />
        </button>
      </div>
    </article>
  );
}
